import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { identityGateway } from '../../identity/services';
import { useStepUpGuard } from '../components/useStepUpGuard';
import type { BeginAuthenticatorEnrollmentResponse, MfaEnrollmentResponse, TrustedDeviceResponse } from '../../identity/types';

/**
 * EPN-49 — real Security Settings for the authenticated caller's own account: Authenticator (TOTP) MFA
 * enrollment/disable, and trusted-device list/revoke. Self-scoped throughout (never another user's) —
 * there is no "manage another user's security" capability anywhere in this slice. Change Password remains
 * linked out to the existing, separately-shipped page rather than duplicated here.
 *
 * Email/SMS MFA, Owner Identity (document/facial) verification, and company-wide security policy toggles
 * shown in the approved design all have no real, operational backend today (email codes are computed but
 * never delivered, SMS has no provider, identity verification has no vendor integration) — shown as an
 * honest "not available" notice, never a toggle with no real effect. See
 * docs/epn-49-owner-security-settings-gaps.md.
 */
export function SecurityWorkspacePage() {
  const { guard, dialog } = useStepUpGuard();
  const [enrollments, setEnrollments] = useState<MfaEnrollmentResponse[]>([]);
  const [devices, setDevices] = useState<TrustedDeviceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [enrollmentList, deviceList] = await Promise.all([
        identityGateway.listMfaEnrollments(),
        identityGateway.listTrustedDevices(),
      ]);
      setEnrollments(enrollmentList);
      setDevices(deviceList);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load your security settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const authenticator = enrollments.find((e) => e.method === 'Authenticator' && e.isEnabled);

  return (
    <section className="card card--wide">
      <h1>Security Settings</h1>
      {dialog}

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <>
          <h2>Login Security</h2>
          <p>
            <Link to="/change-password">Change your password →</Link>
          </p>

          <AuthenticatorSection enrollment={authenticator ?? null} onChanged={load} guard={guard} />

          <p className="muted">
            Email and SMS verification are not available — Email verification codes are not currently
            delivered by a real provider, and SMS has no provider configured at all.
          </p>

          <h2>Your Devices</h2>
          <TrustedDevicesSection devices={devices} onChanged={load} guard={guard} />

          <h2>Owner Identity Verification</h2>
          <p className="muted">
            Not available — document and facial identity verification require a real verification provider
            that is not yet integrated.
          </p>
        </>
      )}
    </section>
  );
}

export type Guard = ReturnType<typeof useStepUpGuard>['guard'];

/** Exported so the Partner Portal's mandatory-MFA screen enrolls with exactly this component instead of a copy of it. */
export function AuthenticatorSection({ enrollment, onChanged, guard }: { enrollment: MfaEnrollmentResponse | null; onChanged: () => Promise<void>; guard: Guard }) {
  const [begin, setBegin] = useState<BeginAuthenticatorEnrollmentResponse | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleStartSetup() {
    setBusy(true);
    setError(null);
    try {
      const started = await guard(() => identityGateway.beginAuthenticatorEnrollment());
      if (started) setBegin(started);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not start Authenticator setup.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm() {
    if (!begin) return;
    setBusy(true);
    setError(null);
    try {
      const done = await guard(async () => {
        await identityGateway.confirmAuthenticatorEnrollment(begin.secret, code);
        return true;
      });
      if (!done) return;
      setBegin(null);
      setCode('');
      await onChanged();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not confirm Authenticator setup.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDisable() {
    if (!window.confirm('Disable Authenticator app verification for your account?')) return;
    setBusy(true);
    setError(null);
    try {
      const done = await guard(async () => {
        await identityGateway.disableAuthenticatorEnrollment();
        return true;
      });
      if (done) await onChanged();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not disable Authenticator verification.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="authenticator-section">
      <h3>Authenticator App</h3>
      {error && <p className="error">{error}</p>}

      {enrollment ? (
        <>
          <p className="success">Enabled since {new Date(enrollment.enrolledAtUtc).toLocaleDateString()}.</p>
          <button type="button" disabled={busy} onClick={() => void handleDisable()}>
            {busy ? 'Disabling…' : 'Disable'}
          </button>
        </>
      ) : begin ? (
        <>
          <p>Scan this with your authenticator app, or enter the code manually:</p>
          <p>
            <code>{begin.secret}</code>
          </p>
          <label>
            Enter the 6-digit code from your app
            <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric" />
          </label>
          <div>
            <button type="button" disabled={busy || code.length !== 6} onClick={() => void handleConfirm()}>
              {busy ? 'Confirming…' : 'Confirm'}
            </button>
            <button type="button" className="link" onClick={() => setBegin(null)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <button type="button" disabled={busy} onClick={() => void handleStartSetup()}>
          {busy ? 'Starting…' : 'Set Up Authenticator App'}
        </button>
      )}
    </div>
  );
}

function TrustedDevicesSection({ devices, onChanged, guard }: { devices: TrustedDeviceResponse[]; onChanged: () => Promise<void>; guard: Guard }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRevoke(deviceId: string) {
    if (!window.confirm('Revoke this device? You will need to verify again the next time you sign in from it.')) return;
    setBusyId(deviceId);
    setError(null);
    try {
      const done = await guard(async () => {
        await identityGateway.revokeTrustedDevice(deviceId);
        return true;
      });
      if (done) await onChanged();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not revoke this device.'));
    } finally {
      setBusyId(null);
    }
  }

  if (devices.length === 0) {
    return <p className="muted">No trusted devices on your account.</p>;
  }

  return (
    <div data-testid="trusted-devices-section">
      {error && <p className="error">{error}</p>}
      <table>
        <thead>
          <tr>
            <th>Added</th>
            <th>Last Used</th>
            <th>IP Address</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.id}>
              <td>{new Date(d.createdAtUtc).toLocaleDateString()}</td>
              <td>{d.lastUsedAtUtc ? new Date(d.lastUsedAtUtc).toLocaleDateString() : '—'}</td>
              <td>{d.createdByIp ?? '—'}</td>
              <td>
                <button type="button" className="link" disabled={busyId === d.id} onClick={() => void handleRevoke(d.id)}>
                  {busyId === d.id ? 'Revoking…' : 'Revoke'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
